import { useState, type FormEvent , useEffect} from 'react';
import { Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { postAuthLogin, postAuthOtpRequest } from '@/api/auth';
import { useAuth } from '@/stores/auth';
import { homeForRole } from '../roles';
import { deviceInfo, errText, getChosenSchool, startSession } from '../shared';
import { useSearchParams } from 'react-router-dom';
import { Fingerprint } from 'lucide-react';
import { askConfirm, notify } from '@/components/ui/ConfirmHost';
import { biometricAvailable, biometricError, disableBiometric, enrolBiometric, enrolledHere, signInWithBiometric } from '@/lib/biometric';
import { isNativeApp } from '@/lib/native';
import { AppSchoolHeader } from '@/components/AppSchoolHeader';

type Mode = 'password' | 'code';

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const isAdminError = searchParams.get('error') === 'use_admin_console';

  // If we arrived here from an OJP admin login attempt, we must clear the stale token
  // so the login page actually renders and doesn't redirect loop.
  useEffect(() => {
    if (isAdminError) {
      localStorage.removeItem('ojp-auth');
      localStorage.removeItem('token');
      localStorage.removeItem('auth_token');
      useAuth.getState().logout(); // Force clear Zustand state
      // Optionally, reload the page once to reset the React Query / Auth state
      // window.location.reload(); // Uncomment if the loop persists
    }
  }, [isAdminError]);


  const user = useAuth((s) => s.user);
  const nav = useNavigate();
  const chosen = getChosenSchool();
  const notice = (useLocation().state as { notice?: string } | null)?.notice ?? null;

  const [mode, setMode] = useState<Mode>('password');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // the same details open more than one account (e.g. a teacher who is also a parent): pick one
  const [choices, setChoices] = useState<{ account_id: number; role: string; label: string; name: string; school: string | null }[] | null>(null);
  // phone app: fingerprint sign-in when it was turned on for this phone
  const [bioUser, setBioUser] = useState(enrolledHere());
  const signInBio = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = await signInWithBiometric();
      startSession(d);
      nav(homeForRole(d.user.role), { replace: true });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) { await disableBiometric(false); setBioUser(null); setError('Fingerprint sign-in was turned off for this phone. Sign in with your password.'); }
      else { const t = biometricError(err); if (t) setError(t); }   // nothing to show when they pressed Cancel
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { if (bioUser && !user && !isAdminError) void signInBio(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** right after a password sign-in on the phone: offer fingerprint for next time (uses the password just typed) */
  const offerBiometric = async (d: any) => {
    if (!isNativeApp || enrolledHere() || d.flags?.must_change_password || !(await biometricAvailable())) return;
    if (!(await askConfirm('Next time, sign in with your fingerprint instead of typing your password. Payments still ask for your PIN.', { title: 'Use your fingerprint?', confirmLabel: 'Turn on' }))) return;
    try {
      await enrolBiometric({ name: d.user.name, role: d.user.role }, { password });
      void notify('Fingerprint sign-in is on for this phone.', { tone: 'success' });
    } catch (err: any) {
      const t = biometricError(err);
      if (t) void notify(t, { tone: 'error' });
    }
  };

  if (user) return <Navigate to={homeForRole(user.role)} replace />;

  const signIn = async (accountId?: number) => {
    setBusy(true);
    setError(null);
    try {
      const r = await postAuthLogin({
        // Silently forward the school code if the user picked one earlier.
        // No visible field — the backend will resolve the account by identifier alone.
        school_code: chosen?.code ?? undefined,
        identifier: identifier.trim(),
        password,
        account_id: accountId,
        device: deviceInfo(),
      });
      const d = r.data.data;
      setChoices(null);
      startSession(d);
      await offerBiometric(d);
      nav(d.flags?.must_change_password ? '/change-password' : homeForRole(d.user.role), { replace: true });
    } catch (err: any) {
      const options = err?.response?.status === 409 ? err.response.data?.data?.choose : null;
      if (Array.isArray(options) && options.length) setChoices(options);
      else setError(errText(err, 'Login failed.'));
    } finally {
      setBusy(false);
    }
  };
  const submitPassword = (e: FormEvent) => { e.preventDefault(); signIn(); };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!identifier.trim()) {
      setError('Enter your phone, email or registration number first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await postAuthOtpRequest({
        school_code: chosen?.code ?? undefined,
        identifier: identifier.trim(),
        purpose: 'login',
      });
      nav('/otp', { state: { identifier: identifier.trim(), schoolCode: chosen?.code ?? '' } });
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'password' ? 'code' : 'password');
    setError(null);
    setPassword('');
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white">

      {/* Left — brand panel */}
      <div className="hidden md:flex flex-col justify-center bg-[#0A2540] px-12 lg:px-20">
        <h1 className="text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
          {chosen?.name ?? 'Myschool Portal'}
        </h1>
        <p className="text-slate-300 mt-4 text-base lg:text-lg">
          Technology for today!
        </p>
      </div>

      {/* Right — form */}
      <div className="flex flex-col justify-center px-6 py-12 md:px-12 lg:px-20">
        <div className="w-full max-w-md mx-auto">
          <AppSchoolHeader subtitle="Parents · Students · Staff" />

          <h2 className="text-3xl font-extrabold text-[#0A2540] mb-8">
            Welcome back
          </h2>

          {notice && (
            <p className="text-sm text-green-700 mb-4" role="status">{notice}</p>
          )}

          {isAdminError && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl shadow-sm">
          <h3 className="font-bold text-amber-900 mb-1 flex items-center gap-2">
            <span className="text-xl">⚠️</span> Wrong Login Portal
          </h3>
          <p className="text-sm text-amber-800 mb-3">
            You are trying to log in as an OJP Platform Administrator. This portal is for School Owners, Staff, Parents, and Students.
          </p>
          <a href="/ojpAdminconsole/" className="inline-flex items-center justify-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold text-sm transition-colors">
            Go to OJP Admin Console &rarr;
          </a>
        </div>
      )}
      
        {choices ? (
          <div className="space-y-3">
            <div>
              <p className="text-base font-bold text-[#0A2540]">Which account do you want to open?</p>
              <p className="text-sm text-slate-500 mt-0.5">These details belong to more than one account.</p>
            </div>
            {choices.map((c) => (
              <button key={c.account_id} type="button" disabled={busy} onClick={() => signIn(c.account_id)}
                className="w-full text-left rounded-xl border border-slate-300 hover:border-[#0A2540] hover:bg-slate-50 px-4 py-3.5 flex items-center gap-3 disabled:opacity-60 transition">
                <span className="w-10 h-10 rounded-full bg-[#0A2540] text-white grid place-items-center text-sm font-bold shrink-0">{c.label.charAt(0)}</span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-slate-900">Sign in as {c.label}</span>
                  <span className="block text-xs text-slate-500 truncate">{c.name}{c.school ? ` · ${c.school}` : ''}</span>
                </span>
                <span className="text-slate-400">→</span>
              </button>
            ))}
            {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
            <button type="button" onClick={() => { setChoices(null); setError(null); }} className="w-full text-sm font-semibold text-slate-600 hover:text-[#0A2540] py-2">Back</button>
          </div>
        ) : (
        <>
        {bioUser && (
          <button type="button" onClick={signInBio} disabled={busy}
            className="w-full mb-6 rounded-xl border-2 border-[#0A2540] bg-[#0A2540]/5 px-4 py-4 flex items-center gap-3 text-left disabled:opacity-60">
            <span className="w-11 h-11 rounded-full bg-[#0A2540] text-white grid place-items-center shrink-0"><Fingerprint className="w-6 h-6" /></span>
            <span className="min-w-0">
              <span className="block font-bold text-[#0A2540]">Sign in with fingerprint</span>
              <span className="block text-xs text-slate-500 truncate">{bioUser.name}</span>
            </span>
          </button>
        )}
        <form
            onSubmit={mode === 'password' ? submitPassword : submitCode}
            className="space-y-5"
            noValidate
          >
            <label className="block">
              <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">
                Phone, email or registration number
              </span>
              <input
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0A2540]/10 transition"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                autoComplete="username"
                placeholder="owner@school.ng"
              />
            </label>

            {mode === 'password' ? (
              <label className="block">
                <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">
                  Password
                </span>
                <input
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0A2540]/10 transition"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
              </label>
            ) : (
              <p className="text-sm text-slate-600 leading-relaxed">
                We'll send a 6-digit code to the phone number on your account.
                SMS rates may apply.
              </p>
            )}

            {error && (
              <p className="text-sm text-red-600" role="alert">{error}</p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-[#0A2540] hover:bg-[#0f2f54] text-white font-semibold py-3.5 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? 'Please wait…' : mode === 'password' ? 'Log in' : 'Send code'}
            </button>

            <button
              type="button"
              disabled={busy}
              onClick={switchMode}
              className="w-full border-2 border-green-700 text-green-800 hover:bg-green-50 font-semibold py-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {mode === 'password' ? 'Use a code instead' : 'Use password instead'}
            </button>

            <div className="flex items-center justify-center gap-2 text-xs text-slate-500 pt-1">
              <Link to="/forgot-password" className="hover:text-[#0A2540] hover:underline">
                Forgot password?
              </Link>
              <span className="text-slate-300">·</span>
              <Link to="/apply" className="hover:text-[#0A2540] hover:underline">
                Apply for admission
              </Link>
              <span className="text-slate-300">·</span>
              <Link to="/privacy" className="hover:text-[#0A2540] hover:underline">
                Privacy
              </Link>
            </div>
          </form>
        </>
        )}

        </div>
      </div>

    </div>
  );
}
